import { DayOfWeek } from "office-ui-fabric-react";
import { Richtext } from "../../components/common/inputs/RichTextInput";
import { IEditable, IWithName, IExtensibleEntity, Dictionary } from "../../entities/common";
import { toDate } from "../../components/utils/common";
import { getDaysBetweenDates } from "../../components/common/timeline/utils";
import { ApplicationState } from "..";

export enum CalendarStatus {
    Draft = 0,
    Active = 1,
    Inactive = 2,
}

export const USER_CALENDAR_NAME = "My Calendar";
const USER_CALENDAR_DESCRIPTION = "Personal calendar for individual work schedule and time-off exceptions.";

export const isUserCalendarId = (calendarId: string | undefined, user: { id: string | null }): boolean => calendarId === String(user.id);

export interface CalendarDataSet {
    exceptions: CalendarException[];
    exceptionsHoursMap: { [id: number]: number };
    workDayExpectedHrs: Dictionary<number>;
    workDayWorkingTimes?: Dictionary<WorkingTimeRange[]>;
    workingHoursPerWeek: number;
    /**
     * US 15956 (ID 11): the share of each day this resource has capacity for PROJECT work
     * (Max Units, % × (100 − Non-project Work, %)), applied by getWorkingHours. Transient and per assignee —
     * only the calendars the scheduling preview builds carry one. Mirrors server Calendar.CapacityFactor.
     */
    capacityFactor?: (date: Date) => number;
    /**
     * Bug 17186: the value signature of `capacityFactor` (ResourceCapacity.key), for memoization. The factor is a
     * closure, so it cannot be compared by identity, and it leaves no trace in the fields above — a resource on the
     * default work week yields a calendar indistinguishable from the global one. Undefined when there is no factor.
     */
    capacityKey?: string;
}

export interface WorkingTimeRange {
    from: number;
    to: number;
}

export type ICalendarAttrs = IWithName & {
    Description: Richtext;
    Status: CalendarStatus;
};

export interface Calendar extends IExtensibleEntity, IEditable, CalendarDataSet {
    isDefault: boolean;
    attributes: ICalendarAttrs;
    userId?: string;
    projectIds: string[];
    resourceIds: string[];
    isUserCalendar?: boolean;
}

export interface CalendarException {
    id: string;
    name: string;
    start: Date;
    end: Date;
    days: DayOfWeek[];
    expectedHrs: number;
    workingTimes?: WorkingTimeRange[];
    description?: Richtext;
}

export type CalendarInfo = {
    id: string;
    name: string;
}

export interface CreateOrUpdateOrDeleteCalendarExceptionsModel {
    toCreate: CalendarException[];
    toUpdate: CalendarException[];
    toDelete: string[];
}

export const canCalendarBeDeleted = (calendar: Calendar) =>
    calendar.isEditable && !calendar.isDefault && !calendar.isUserCalendar
    && !calendar.projectIds?.length
    && !calendar.resourceIds?.length;

export const getDeleteCalendarTitle = (calendar: Calendar): string | undefined => {
    if (canCalendarBeDeleted(calendar)) {
        return undefined;
    }

    const reasons = [
        calendar.isDefault ? 'set as default' : undefined,
        calendar.projectIds?.length || calendar.resourceIds?.length ? 'associated with projects and/or resources' : undefined,
        calendar.isUserCalendar ? 'a user calendar' : undefined,
    ].filter(_ => !!_);

    return reasons.length ? `Calendar cannot be deleted, because it is ${reasons.join(' and ')}.` : undefined;
};

export function checkExceptionDates(exceptions: CalendarException[]): CalendarException[] {
    return exceptions.map(_ => ({
        ..._,
        start: toDate(_.start)!.getBeginOfDay(),
        end: toDate(_.end)!.getBeginOfDay()
    }));
}

//similar method is on server side in CalendarExceptionExtensions
export function splitToDays(exceptions: {
    start: Date;
    end: Date;
    days: DayOfWeek[];
    expectedHrs: number;
}[]): { date: Date, isWorkingDay: boolean, expectedHrs: number }[] {
    return exceptions
        .flatMap(_ => getDaysBetweenDates(_.start, _.end)
            .filter(__ => _.days.includes(__.getDay()))
            .map(__ => ({ date: __, isWorkingDay: _.expectedHrs > 0, expectedHrs: _.expectedHrs })))
        .reduce((cum, cur) => {
            const key = cur.date.getTime();
            if (!cum.set.has(key)) {
                cum.set.add(key);
                cum.arr.push(cur);
            }
            return cum;
        }, { set: new Set(), arr: [] as { date: Date, isWorkingDay: boolean, expectedHrs: number }[] })
        .arr;
}

export const prepareCalendar = (calendar: Calendar): Calendar => {
    if (calendar?.exceptions) {
        calendar.exceptions = checkExceptionDates(calendar.exceptions);
        calendar.exceptionsHoursMap = splitToDays(calendar.exceptions).reduce((cum, _) => ({ ...cum, [_.date.getTime()]: _.expectedHrs }), {});
    }

    if (calendar?.workDayExpectedHrs) {
        calendar.workingHoursPerWeek = Object.keys(calendar.workDayExpectedHrs).reduce((sum, _) => sum + calendar.workDayExpectedHrs[_], 0);
    }

    return calendar;
}

export const getUserCalendar = (state: ApplicationState): Calendar => {
    const resourceCalendar = (state.resources.activeEntity ?? state.user.resource)?.usage?.calendar;
    return prepareCalendar({
        id: state.user.id!,
        isDefault: false,
        isEditable: true,
        isUserCalendar: true,
        projectIds: [],
        resourceIds: [],
        attributes: {
            Name: USER_CALENDAR_NAME,
            Status: CalendarStatus.Active,
            Description: { html: USER_CALENDAR_DESCRIPTION, text: USER_CALENDAR_DESCRIPTION }
        },
        exceptions: resourceCalendar?.exceptions ?? [],
        workDayExpectedHrs: resourceCalendar?.workDayExpectedHrs ?? {},
        workDayWorkingTimes: resourceCalendar?.workDayWorkingTimes ?? {},
        workingHoursPerWeek: 0,
        exceptionsHoursMap: {},
    });
};